import { createFileRoute } from "@tanstack/react-router";
import { AlertTriangle, ArrowRight, CheckCircle2, MessageCircle } from "lucide-react";

import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/ui";

type SlackInstallSearch = {
  status?: "success" | "error" | "revoked";
  mode?: "add" | "reconnect";
  team?: string;
  message?: string;
  next?: string;
};

function sanitizeNextPath(nextPath: string | undefined): string {
  if (!nextPath) return "/integrations";
  if (!nextPath.startsWith("/") || nextPath.startsWith("//")) return "/integrations";
  return nextPath;
}

export const Route = createFileRoute("/_public/slack/install")({
  validateSearch: (search: Record<string, unknown>): SlackInstallSearch => ({
    status:
      search.status === "success" || search.status === "error" || search.status === "revoked"
        ? search.status
        : undefined,
    mode: search.mode === "add" || search.mode === "reconnect" ? search.mode : undefined,
    team: typeof search.team === "string" ? search.team : undefined,
    message: typeof search.message === "string" ? search.message : undefined,
    next: typeof search.next === "string" ? search.next : undefined,
  }),
  component: SlackInstallStatusPage,
});

function SlackInstallStatusPage() {
  const search = Route.useSearch();
  const nextPath = sanitizeNextPath(search.next);
  const status = search.status ?? "error";
  const mode = search.mode ?? "add";
  const isSuccess = status === "success";
  const isRevoked = status === "revoked";
  const isReconnect = mode === "reconnect";

  const title = isSuccess
    ? isReconnect
      ? "Slack workspace reconnected"
      : "Slack workspace added"
    : isRevoked
      ? "Slack app needs reconnection"
      : isReconnect
        ? "Slack reconnect failed"
        : "Slack install failed";
  const subtitle = isSuccess
    ? isReconnect
      ? "Your Slack workspace is active again."
      : "Your workspace is now connected to Veritab."
    : isRevoked
      ? "Your previous token was revoked and must be reconnected."
      : isReconnect
        ? "We couldn't complete your Slack workspace reconnection."
        : "We couldn't complete your Slack installation.";
  const details =
    search.message ??
    (isSuccess
      ? isReconnect
        ? "You can continue using Veritab in Slack DMs."
        : "You can now use Veritab in Slack DMs."
      : isRevoked
        ? "Reconnect from Integrations to restore Slack messaging."
        : isReconnect
          ? "Please retry the reconnect flow from Integrations."
          : "Please retry the installation flow.");

  return (
    <div className="relative min-h-full overflow-hidden bg-[radial-gradient(circle_at_top,_#f4ecff_0%,_#f8fafc_40%,_#ffffff_100%)] p-6 md:p-10">
      <div className="pointer-events-none absolute -top-24 right-0 h-72 w-72 rounded-full bg-[#4A154B]/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-32 left-0 h-80 w-80 rounded-full bg-emerald-500/10 blur-3xl" />

      <div className="mx-auto flex min-h-[70vh] w-full max-w-2xl items-center justify-center">
        <Card className="w-full border-0 bg-white/85 shadow-xl backdrop-blur">
          <CardHeader className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#4A154B] text-white">
                <MessageCircle className="size-5" />
              </div>
              <div className="flex items-center gap-2">
                {isSuccess ? (
                  <CheckCircle2 className="size-5 text-emerald-600" />
                ) : (
                  <AlertTriangle className="size-5 text-amber-600" />
                )}
                <CardTitle className="text-2xl">{title}</CardTitle>
              </div>
            </div>
            <CardDescription className="text-base text-slate-700">{subtitle}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="rounded-xl border bg-slate-50/80 p-4 text-sm text-slate-700">
              <p>{details}</p>
              {search.team && (
                <p className="mt-2 text-slate-500">
                  Workspace: <span className="font-medium text-slate-700">{search.team}</span>
                </p>
              )}
            </div>
            <div className="flex flex-col gap-3 sm:flex-row">
              <Button className="sm:flex-1" onClick={() => (window.location.href = nextPath)}>
                Continue
                <ArrowRight className="ml-2 size-4" />
              </Button>
              {!isSuccess && (
                <Button variant="outline" className="sm:flex-1" onClick={() => (window.location.href = "/integrations")}>
                  Retry from Integrations
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
